import { useEffect, useMemo, useRef, useState } from "react";
import type { MessageBodyCacheStatus } from "@/features/inbox/MessageBodyCacheBadge";
import { INBOX_BODY_STATUS_RETRY_MS, INBOX_CACHE_WINDOW_SIZE } from "@/lib/constants/inbox";
import { reconcileVisibleBodyFlags } from "@/lib/mail/bodyCacheReconcile";
import { isMessageBodyCached } from "@/lib/mail/messageBodyCacheStatus";
import {
  MAIL_BODY_WARMED_EVENT,
  type MailBodyWarmedDetail,
} from "@/lib/mail/mailBodyWarmEvents";
import {
  MAIL_BODY_WARMING_EVENT,
  type MailBodyWarmingDetail,
  getWarmingUids,
  resolveMessageBodyCacheStatus,
  subscribeBodyWarmState,
} from "@/lib/mail/mailBodyWarmState";
import {
  subscribeMessageBodyCache,
  primeMessageBodyCacheFromIndexedDB,
} from "@/lib/cache/messageBodyCache";

export type BodyCacheEntry = {
  uid: number;
  folder: string;
  bodyCached?: boolean;
  /** Clé liste si uid ≠ uid corps (vue En attente). */
  listUid?: number;
};

function mergeFlagMaps(
  prev: Map<string, boolean>,
  next: Map<string, boolean>
): Map<string, boolean> {
  if (next.size === 0) return prev;
  let changed = false;
  const merged = new Map(prev);
  for (const [key, value] of next) {
    if (merged.get(key) !== value) {
      changed = true;
      merged.set(key, value);
    }
  }
  return changed ? merged : prev;
}

/** Pastilles corps pour une liste multi-dossiers (vue En attente). */
export function useMultiFolderBodyCacheFlags(
  accountId: number | undefined,
  entries: BodyCacheEntry[]
): Map<number, MessageBodyCacheStatus> {
  const [clientVersion, setClientVersion] = useState(0);
  const [warmVersion, setWarmVersion] = useState(0);
  const [serverFlags, setServerFlags] = useState<Map<string, boolean>>(new Map());
  const entryKey = useMemo(
    () => entries.map((e) => `${e.folder}\0${e.uid}`).join("|"),
    [entries]
  );
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const fetchGen = useRef(0);
  const retryGen = useRef(0);

  useEffect(() => {
    return subscribeMessageBodyCache(() => setClientVersion((v) => v + 1));
  }, []);

  useEffect(() => {
    return subscribeBodyWarmState(() => setWarmVersion((v) => v + 1));
  }, []);

  useEffect(() => {
    const onBodiesWarming = (event: Event) => {
      const detail = (event as CustomEvent<MailBodyWarmingDetail>).detail;
      if (!detail || detail.accountId !== accountId) return;
      setWarmVersion((v) => v + 1);
    };
    const onBodiesWarmed = (event: Event) => {
      const detail = (event as CustomEvent<MailBodyWarmedDetail>).detail;
      if (!detail || detail.accountId !== accountId) return;
      setWarmVersion((v) => v + 1);
    };
    window.addEventListener(MAIL_BODY_WARMING_EVENT, onBodiesWarming);
    window.addEventListener(MAIL_BODY_WARMED_EVENT, onBodiesWarmed);
    return () => {
      window.removeEventListener(MAIL_BODY_WARMING_EVENT, onBodiesWarming);
      window.removeEventListener(MAIL_BODY_WARMED_EVENT, onBodiesWarmed);
    };
  }, [accountId]);

  useEffect(() => {
    if (accountId == null || entryKey.length === 0) {
      setServerFlags(new Map());
      return;
    }

    const currentEntries = entriesRef.current;
    const byFolder = new Map<string, number[]>();
    for (const entry of currentEntries) {
      if (entry.uid <= 0) continue;
      const list = byFolder.get(entry.folder) ?? [];
      if (list.length < INBOX_CACHE_WINDOW_SIZE) {
        list.push(entry.uid);
      }
      byFolder.set(entry.folder, list);
    }

    retryGen.current += 1;
    const localRetryGen = retryGen.current;

    for (const [folder, folderUids] of byFolder) {
      void primeMessageBodyCacheFromIndexedDB(accountId, folder, folderUids);
    }

    const loadFlags = async (): Promise<boolean> => {
      const gen = ++fetchGen.current;
      const merged = new Map<string, boolean>();
      let anyOk = false;

      for (const [folder, folderUids] of byFolder) {
        const flags = await reconcileVisibleBodyFlags({
          accountId,
          folder,
          uids: folderUids,
          force: true,
        });
        if (gen !== fetchGen.current) return false;
        if (!flags) continue;
        anyOk = true;
        for (const [uid, value] of flags) {
          merged.set(`${folder}\0${uid}`, value);
        }
      }

      if (gen !== fetchGen.current) return false;
      if (anyOk) {
        setServerFlags((prev) => mergeFlagMaps(prev, merged));
      }

      return anyOk;
    };

    void (async () => {
      const ok = await loadFlags();
      if (!ok) return;

      for (const delay of INBOX_BODY_STATUS_RETRY_MS) {
        window.setTimeout(() => {
          if (localRetryGen !== retryGen.current) return;
          void loadFlags();
        }, delay);
      }
    })();
  }, [accountId, entryKey]);

  return useMemo(() => {
    const map = new Map<number, MessageBodyCacheStatus>();
    if (accountId == null) return map;

    for (const entry of entries) {
      if (entry.uid <= 0) continue;
      const mapKey = entry.listUid ?? entry.uid;
      const flagKey = `${entry.folder}\0${entry.uid}`;
      const warmingNow = getWarmingUids(accountId, entry.folder).has(entry.uid);
      const fromList = entry.bodyCached === true;
      map.set(
        mapKey,
        resolveMessageBodyCacheStatus({
          fromList,
          warmingNow,
          clientCached: isMessageBodyCached(accountId, entry.folder, entry.uid),
          serverCached: serverFlags.get(flagKey) ?? false,
        })
      );
    }

    return map;
  }, [accountId, clientVersion, entries, entryKey, serverFlags, warmVersion]);
}
